/**
 * Vue 3 + @vue/compat fix for bootstrap-vue `v-b-tooltip`.
 *
 * BV's updateData() ignores undefined fields, so when `:title` goes from a
 * string to undefined the previous title sticks. Normalize empty titles to
 * `''` and disable the overlay so updates actually clear.
 */
import { VBTooltip } from "bootstrap-vue";

function isPlainObject(value) {
	return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function getVnodeTitle(vnode) {
	const props = vnode && vnode.props;
	if (props && props.title != null) {
		return props.title;
	}
	return undefined;
}

function resolveTitle(binding, vnode) {
	const value = binding.value;
	if (typeof value === "function") {
		return value;
	}
	if (typeof value === "string" || typeof value === "number") {
		return value;
	}
	if (isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, "title")) {
		return value.title;
	}
	return getVnodeTitle(vnode);
}

function isEmptyTitle(title) {
	if (title == null) {
		return true;
	}
	if (typeof title === "function") {
		return false;
	}
	return String(title).trim() === "";
}

function normalizeBinding(binding, vnode) {
	const title = resolveTitle(binding, vnode);
	if (!isEmptyTitle(title)) {
		return binding;
	}

	const base = isPlainObject(binding.value) ? { ...binding.value } : {};
	return {
		...binding,
		value: {
			...base,
			title: "",
			disabled: true,
		},
	};
}

function getHook(dir, vue3Name) {
	if (typeof dir[vue3Name] === "function") {
		return dir[vue3Name];
	}
	if (vue3Name === "beforeMount" && typeof dir.bind === "function") {
		return dir.bind;
	}
	if (vue3Name === "updated") {
		return dir.updated || dir.componentUpdated || null;
	}
	if (vue3Name === "unmounted") {
		return dir.unmounted || dir.unbind || null;
	}
	return null;
}

function wrapDirective(original) {
	const call = (vue3Name) => (el, binding, vnode, prevVnode) => {
		const hook = getHook(original, vue3Name);
		if (!hook) {
			return;
		}
		hook(el, normalizeBinding(binding, vnode), vnode, prevVnode);
	};

	// Modern Vue 3 hooks only — avoids double-firing under @vue/compat.
	return {
		beforeMount: call("beforeMount"),
		updated: call("updated"),
		unmounted: call("unmounted"),
	};
}

export function installBootstrapTooltipCompat(app) {
	const wrapped = wrapDirective(VBTooltip);
	// BV registers VBTooltip as BTooltip; templates use v-b-tooltip.
	app.directive("BTooltip", wrapped);
	app.directive("b-tooltip", wrapped);
}
